
import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from "react";
import { User, Session } from '@supabase/supabase-js';
import { toast } from "@/hooks/use-toast";
import { AuthService } from "@/services/auth/AuthService";
import { RepositoryFactory } from "@/shared/infrastructure/RepositoryFactory";

interface AuthContextType {
  isAuthenticated: boolean | null;
  user: User | null;
  session: Session | null;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  signup: (email: string, password: string, metadata?: { full_name?: string, whatsapp_number?: string }) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
}

const AuthContextV2 = createContext<AuthContextType | undefined>(undefined);

export const useAuthV2 = () => {
  const context = useContext(AuthContextV2);
  if (context === undefined) {
    throw new Error("useAuthV2 must be used within an AuthProviderV2");
  }
  return context;
};

export const AuthProviderV2 = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Initialize auth service with dependency injection
  const authService = useMemo(() => new AuthService(), []);

  const login = useCallback(async (email: string, password: string, rememberMe: boolean = false) => {
    try {
      setLoading(true);
      console.log('🔐 Iniciando login via AuthContextV2...');
      
      const result = await authService.signIn({ email, password });
      
      if (result.error) {
        throw new Error(result.error);
      }
      
      if (result.user && result.session) {
        setUser(result.user);
        setSession(result.session);
        setIsAuthenticated(true);
        
        // Save preference if "lembrar de mim" is active
        if (rememberMe) {
          localStorage.setItem('rememberMe', 'true');
        } else {
          localStorage.removeItem('rememberMe');
        }
        
        console.log("Login bem-sucedido via AuthService");
      }
    } catch (error: any) {
      console.error("Erro no login:", error.message);
      toast({
        title: "Erro no login",
        description: error.message || "Não foi possível fazer login. Tente novamente.",
        variant: "destructive",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  }, [authService]);
  
  const signup = useCallback(async (email: string, password: string, metadata?: { full_name?: string, whatsapp_number?: string }) => {
    try {
      setLoading(true);
      console.log('📝 Iniciando cadastro via AuthContextV2...');
      
      const result = await authService.signUp({ email, password, fullName: metadata?.full_name });
      
      if (result.error) {
        throw new Error(result.error);
      }
      
      console.log("Cadastro bem-sucedido via AuthService");
      
      toast({
        title: "Cadastro realizado",
        description: "Sua conta foi criada com sucesso.",
      });
    } catch (error: any) {
      console.error("Erro no cadastro:", error.message);
      toast({
        title: "Erro no cadastro",
        description: error.message || "Não foi possível criar sua conta. Tente novamente.",
        variant: "destructive",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  }, [authService]);
  
  const logout = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🚪 Iniciando logout via AuthContextV2...');
      
      const result = await authService.signOut();
      
      if (result.error) {
        throw new Error(result.error);
      }
      
      // Clear state
      setIsAuthenticated(false);
      setUser(null);
      setSession(null);
      
      // Remove remember me preference
      localStorage.removeItem('rememberMe');
      
      console.log("Logout bem-sucedido via AuthService");
      
      toast({
        title: "Logout realizado",
        description: "Você saiu da sua conta com sucesso.",
      });
    } catch (error: any) {
      console.error("Erro no logout:", error.message);
      toast({
        title: "Erro no logout",
        description: "Ocorreu um erro ao sair. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [authService]);

  useEffect(() => {
    const initAuth = async () => {
      try {
        setLoading(true);
        console.log("Inicializando AuthContextV2 com AuthService...");
        
        // Set up auth state change listener
        const unsubscribe = authService.onAuthStateChange((event, currentSession) => {
          console.log("Mudança de estado de autenticação V2:", event, !!currentSession);
          
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
          setIsAuthenticated(!!currentSession);
          setLoading(false);
        });
        
        // Check for existing session
        const currentSession = await authService.getCurrentSession();
        const currentUser = await authService.getCurrentUser();
        
        setSession(currentSession);
        setUser(currentUser);
        setIsAuthenticated(!!currentSession && !!currentUser);
        
        console.log("AuthContextV2 inicializado, sessão:", !!currentSession);
        
        return unsubscribe;
      } catch (error) {
        console.error("Erro ao inicializar AuthContextV2:", error);
        setIsAuthenticated(false);
        setUser(null);
        setSession(null);
      } finally {
        setLoading(false);
      }
    };
    
    const cleanup = initAuth();
    
    return () => {
      cleanup.then(unsubscribe => unsubscribe?.());
    };
  }, [authService]);

  const contextValue = useMemo(() => ({
    isAuthenticated,
    user,
    session,
    login,
    signup,
    logout,
    loading
  }), [isAuthenticated, user, session, login, signup, logout, loading]);

  return (
    <AuthContextV2.Provider value={contextValue}>
      {children}
    </AuthContextV2.Provider>
  );
};
